import { AbstractClient, CompositionClient } from "@/app/client";
import type {
  SuperAdminCompanyResponse,
  SuperAdminCompanyCreate,
  SuperAdminCompanyUpdate,
  SuperAdminUserResponse,
  SuperAdminUserCreate,
  SuperAdminUserUpdate,
  SuperAdminEmployeeResponse,
  SuperAdminEmployeeCreate,
  SuperAdminEmployeeUpdate,
  SuperAdminEmployeeLinkRequest,
  ImpersonationResponse,
  FeatureFlagDefinition,
  BulkFeatureFlagUpdate,
  FeatureFlagsResponse,
  SuperAdminStats,
  PaginatedCompaniesResponse,
  PaginatedUsersResponse,
  PaginatedEmployeesResponse,
  ListCompaniesParams,
  ListUsersParams,
  ListEmployeesParams,
  SuperAdminGroupCreate,
  SuperAdminGroupRename,
  PaginatedGroupsResponse,
  ListGroupsParams,
  CompanyMessagingResponse,
  MessagingChannel,
  MessagingChannelType,
  ChannelAssignmentUpdate,
  EmailChannelCreate,
  SmsChannelCreate,
} from "@/app/api/types/superadmin";
import type { GroupResponse } from "@/app/api/types/group";

export default class APISuperAdminSub extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  // --- Statistics ---

  getStats = async (): Promise<SuperAdminStats> => {
    const res = await this._fetch({
      url: `/superadmin/stats`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  // --- Company Management ---

  listCompanies = async (
    params: ListCompaniesParams = {},
  ): Promise<PaginatedCompaniesResponse> => {
    const res = await this._fetch({
      url: `/superadmin/companies`,
      method: "GET",
      params: {
        search: params.search,
        page: params.page,
        size: params.size,
        include_inactive: params.include_inactive,
      },
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  getCompany = async (
    companyId: string,
  ): Promise<SuperAdminCompanyResponse> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  createCompany = async (
    data: SuperAdminCompanyCreate,
  ): Promise<SuperAdminCompanyResponse> => {
    const res = await this._fetch({
      url: `/superadmin/companies`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  updateCompany = async (
    companyId: string,
    data: SuperAdminCompanyUpdate,
  ): Promise<SuperAdminCompanyResponse> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}`,
      method: "PATCH",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  deleteCompany = async (companyId: string): Promise<{ message: string }> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  // --- User Management ---

  listUsers = async (
    params: ListUsersParams = {},
  ): Promise<PaginatedUsersResponse> => {
    const res = await this._fetch({
      url: `/superadmin/users`,
      method: "GET",
      params: {
        search: params.search,
        company_id: params.company_id,
        page: params.page,
        size: params.size,
        include_inactive: params.include_inactive,
      },
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  getUser = async (userId: string): Promise<SuperAdminUserResponse> => {
    const res = await this._fetch({
      url: `/superadmin/users/${userId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  createUser = async (
    data: SuperAdminUserCreate,
  ): Promise<SuperAdminUserResponse> => {
    const res = await this._fetch({
      url: `/superadmin/users`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  updateUser = async (
    userId: string,
    data: SuperAdminUserUpdate,
  ): Promise<SuperAdminUserResponse> => {
    const res = await this._fetch({
      url: `/superadmin/users/${userId}`,
      method: "PATCH",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  deleteUser = async (userId: string): Promise<{ message: string }> => {
    const res = await this._fetch({
      url: `/superadmin/users/${userId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  // --- Employee Management ---

  listEmployees = async (
    params: ListEmployeesParams = {},
  ): Promise<PaginatedEmployeesResponse> => {
    const res = await this._fetch({
      url: `/superadmin/employees`,
      method: "GET",
      params: {
        search: params.search,
        company_id: params.company_id,
        page: params.page,
        size: params.size,
      },
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  createEmployee = async (
    data: SuperAdminEmployeeCreate,
  ): Promise<SuperAdminEmployeeResponse> => {
    const res = await this._fetch({
      url: `/superadmin/employees`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  updateEmployee = async (
    employeeId: string,
    data: SuperAdminEmployeeUpdate,
  ): Promise<SuperAdminEmployeeResponse> => {
    const res = await this._fetch({
      url: `/superadmin/employees/${employeeId}`,
      method: "PATCH",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  deleteEmployee = async (employeeId: string): Promise<{ message: string }> => {
    const res = await this._fetch({
      url: `/superadmin/employees/${employeeId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  linkEmployee = async (
    employeeId: string,
    data: SuperAdminEmployeeLinkRequest,
  ): Promise<SuperAdminEmployeeResponse> => {
    const res = await this._fetch({
      url: `/superadmin/employees/${employeeId}/link`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  unlinkEmployee = async (
    employeeId: string,
  ): Promise<SuperAdminEmployeeResponse> => {
    const res = await this._fetch({
      url: `/superadmin/employees/${employeeId}/unlink`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  // --- Impersonation ---

  impersonateUser = async (userId: string): Promise<ImpersonationResponse> => {
    const res = await this._fetch({
      url: `/superadmin/users/${userId}/impersonate`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  resetUserPassword = async (userId: string): Promise<{ message: string }> => {
    const res = await this._fetch({
      url: `/superadmin/users/${userId}/reset-password`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  setUserPassword = async (
    userId: string,
    password: string,
  ): Promise<{ message: string }> => {
    const res = await this._fetch({
      url: `/superadmin/users/${userId}/set-password`,
      method: "POST",
      body: { password },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  // --- Feature Flags ---

  listFeatureFlags = async (): Promise<FeatureFlagDefinition[]> => {
    const res = await this._fetch({
      url: `/superadmin/feature-flags`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  updateCompanyFlags = async (
    companyId: string,
    data: BulkFeatureFlagUpdate,
  ): Promise<FeatureFlagsResponse> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}/feature-flags`,
      method: "PUT",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  updateUserFlags = async (
    userId: string,
    data: BulkFeatureFlagUpdate,
  ): Promise<FeatureFlagsResponse> => {
    const res = await this._fetch({
      url: `/superadmin/users/${userId}/feature-flags`,
      method: "PUT",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  // --- Group Management ---

  listGroups = async (
    params: ListGroupsParams = {},
  ): Promise<PaginatedGroupsResponse> => {
    const res = await this._fetch({
      url: `/superadmin/groups`,
      method: "GET",
      params: {
        search: params.search,
        company_id: params.company_id,
        page: params.page,
        size: params.size,
      },
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  listCompanyGroups = async (companyId: string): Promise<GroupResponse[]> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}/groups`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  getCompanyGroup = async (
    companyId: string,
    groupId: string,
  ): Promise<GroupResponse> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}/groups/${groupId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  createCompanyGroup = async (
    companyId: string,
    data: SuperAdminGroupCreate,
  ): Promise<GroupResponse> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}/groups`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  renameCompanyGroup = async (
    companyId: string,
    groupId: string,
    data: SuperAdminGroupRename,
  ): Promise<GroupResponse> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}/groups/${groupId}`,
      method: "PATCH",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  deleteCompanyGroup = async (
    companyId: string,
    groupId: string,
  ): Promise<{ message: string }> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}/groups/${groupId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  addCompanyGroupMember = async (
    companyId: string,
    groupId: string,
    userId: string,
  ): Promise<GroupResponse> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}/groups/${groupId}/members`,
      method: "POST",
      body: { user_id: userId },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  removeCompanyGroupMember = async (
    companyId: string,
    groupId: string,
    userId: string,
  ): Promise<GroupResponse> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}/groups/${groupId}/members/${userId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  updateCompanyGroupMemberRole = async (
    companyId: string,
    groupId: string,
    userId: string,
    role: 0 | 1,
  ): Promise<GroupResponse> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}/groups/${groupId}/members/${userId}/role`,
      method: "PATCH",
      body: { role },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  setCompanyGroupOwner = async (
    companyId: string,
    groupId: string,
    userId: string,
  ): Promise<GroupResponse> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}/groups/${groupId}/owner`,
      method: "POST",
      body: { user_id: userId },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  // --- Messaging Channel Management ---

  getCompanyMessagingChannels = async (
    companyId: string,
  ): Promise<CompanyMessagingResponse> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}/messaging-channels`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  updateMessagingChannelAssignment = async (
    companyId: string,
    channelType: MessagingChannelType,
    channelId: string,
    data: ChannelAssignmentUpdate,
  ): Promise<MessagingChannel> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}/messaging-channels/${channelType}/${encodeURIComponent(
        channelId,
      )}`,
      method: "PATCH",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  createEmailChannel = async (
    companyId: string,
    data: EmailChannelCreate,
  ): Promise<MessagingChannel> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}/messaging-channels/email`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  createSmsChannel = async (
    companyId: string,
    data: SmsChannelCreate,
  ): Promise<MessagingChannel> => {
    const res = await this._fetch({
      url: `/superadmin/companies/${companyId}/messaging-channels/sms`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.json();
  };

  deleteMessagingChannel = async (
    companyId: string,
    channelType: MessagingChannelType,
    channelId: string,
  ): Promise<void> => {
    await this._fetch({
      url: `/superadmin/companies/${companyId}/messaging-channels/${channelType}/${encodeURIComponent(
        channelId,
      )}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
  };
}
